import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, Star, ExternalLink, Check, X, Shield, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { SEO, SchemaOrg } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PartnerDetailPage = () => {
  const { slug } = useParams();
  const [partner, setPartner] = useState(null);
  const [allPartners, setAllPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [partnerRes, allPartnersRes] = await Promise.all([
          axios.get(`${API}/partners/${slug}`),
          axios.get(`${API}/partners`)
        ]);
        setPartner(partnerRes.data);
        setAllPartners(allPartnersRes.data);
      } catch (error) {
        console.error('Error fetching partner:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-stone-300 border-t-stone-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="heading-2 text-stone-900 mb-4">Partner Not Found</h1>
        <Link to="/partners" className="btn-primary">View All Partners</Link>
      </div>
    );
  }

  const otherPartners = allPartners.filter(p => p.slug !== slug);

  return (
    <div data-testid={`partner-page-${slug}`}>
      <SEO
        title={`${partner.name} Review — Pre-Owned Jewelry`}
        description={partner.description}
        image={partner.logo_url}
        url={`/partners/${partner.slug}`}
        type="article"
      />
      <SchemaOrg
        type="breadcrumb"
        data={{
          items: [
            { name: 'Home', url: '/' },
            { name: 'Partners', url: '/partners' },
            { name: partner.name, url: `/partners/${partner.slug}` },
          ],
        }}
      />

      {/* Breadcrumb */}
      <div className="bg-stone-50 border-b border-stone-200 py-4">
        <div className="container-luxury">
          <nav className="breadcrumb">
            <Link to="/" className="breadcrumb-link">Home</Link>
            <span className="breadcrumb-separator">/</span>
            <Link to="/partners" className="breadcrumb-link">Partners</Link>
            <span className="breadcrumb-separator">/</span>
            <span className="text-stone-900">{partner.name}</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="section-padding bg-stone-50">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-4 mb-4">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i}
                      className={`w-5 h-5 ${i < Math.floor(partner.rating) ? 'text-amber-500 fill-amber-500' : 'text-stone-300'}`}
                    />
                  ))}
                </div>
                <span className="text-lg font-medium text-stone-900">{partner.rating}/5</span>
              </div>
              <h1 className="heading-1 text-stone-900 mb-4">{partner.name}</h1>
              <p className="text-lg text-amber-800 font-medium mb-6">{partner.specialty}</p>
              <p className="body-lg mb-8">{partner.description}</p>
              <a 
                href={partner.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="partner-cta-btn"
              >
                Shop on {partner.name}
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
            <div className="bg-white border border-stone-200 p-8">
              <h3 className="font-heading text-xl text-stone-900 mb-6">At a Glance</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-stone-100">
                  <span className="text-stone-600">Authentication</span>
                  <div className="flex items-center gap-2 text-green-600">
                    <Check className="w-5 h-5" />
                    <span className="font-medium">Expert Team</span>
                  </div>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-stone-100">
                  <span className="text-stone-600">Return Policy</span>
                  <span className="font-medium text-stone-900">
                    {slug === 'fashionphile' ? '30 days' :
                     slug === 'therealreal' ? '14 days' :
                     slug === '1stdibs' ? 'Varies by dealer' : '14 days'}
                  </span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-stone-100">
                  <span className="text-stone-600">Shipping</span>
                  <span className="font-medium text-stone-900">
                    {slug === '1stdibs' ? 'Global' : 'US & International'}
                  </span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-stone-600">Payment Plans</span>
                  <span className="font-medium text-stone-900">
                    {slug === 'fashionphile' ? 'Yes (Affirm)' : 
                     slug === 'therealreal' ? 'Yes (Affirm)' : 'Limited'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pros & Cons */}
      <section className="section-padding">
        <div className="container-luxury">
          <h2 className="heading-3 text-stone-900 mb-8 text-center">Pros & Cons</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-green-50 border border-green-200 p-8">
              <h3 className="flex items-center gap-2 font-heading text-xl text-green-800 mb-6">
                <Check className="w-6 h-6" />
                Pros
              </h3>
              <ul className="space-y-4">
                {partner.pros.map((pro, index) => (
                  <li key={index} className="flex items-start gap-3 text-stone-700">
                    <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    {pro}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-red-50 border border-red-200 p-8">
              <h3 className="flex items-center gap-2 font-heading text-xl text-red-800 mb-6">
                <X className="w-6 h-6" />
                Cons
              </h3>
              <ul className="space-y-4">
                {partner.cons.map((con, index) => (
                  <li key={index} className="flex items-start gap-3 text-stone-700">
                    <X className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    {con}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why We Recommend */}
      <section className="section-padding bg-stone-50">
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto">
            <h2 className="heading-3 text-stone-900 mb-6 text-center">Why We Recommend {partner.name}</h2>
            <div className="prose-luxury">
              <p>
                {slug === 'fashionphile' && 
                  "Fashionphile stands out for its industry-leading authentication process and exceptional condition grading accuracy. With over 20 years in the luxury resale market, they've built a reputation for quality and trustworthiness. Their team of expert authenticators examines every piece, and their detailed photography and descriptions give you confidence in your purchase. The 30-day return policy provides extra peace of mind for high-value jewelry purchases."}
                {slug === 'therealreal' && 
                  "The RealReal offers one of the largest selections of pre-owned luxury jewelry available online. Their sustainability mission resonates with conscious luxury shoppers, and their competitive pricing—especially during frequent sales—makes luxury more accessible. While condition accuracy can vary, their authentication team provides a solid foundation of trust, and their consignment model means new inventory arrives constantly."}
                {slug === '1stdibs' && 
                  "1stDibs is the destination for serious collectors and those seeking exceptional vintage and antique pieces. Their network of vetted professional dealers from around the world offers access to rare finds you won't see elsewhere. While prices tend toward the premium end, the quality and uniqueness of pieces available makes it worthwhile for special purchases. The buyer protection program adds security to transactions."}
                {slug === 'vestiaire-collective' && 
                  "Vestiaire Collective brings a community-driven approach to luxury resale with strong European roots. The platform excels in French and Italian luxury, with direct seller communication enabling price negotiation. Their quality control process ensures standards are maintained, and the sustainability focus appeals to conscious shoppers. International shipping and a diverse, ever-changing inventory make it worth regular browsing."}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-stone-900">
        <div className="container-luxury text-center">
          <h2 className="heading-3 text-white mb-4">Ready to Shop?</h2>
          <p className="body-lg text-stone-300 mb-8 max-w-2xl mx-auto">
            Discover authenticated pre-owned luxury jewelry on {partner.name}.
          </p>
          <a 
            href={partner.url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary bg-white text-stone-900 hover:bg-stone-100 inline-flex items-center gap-2"
          >
            Visit {partner.name}
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* Other Partners */}
      <section className="section-padding">
        <div className="container-luxury">
          <h2 className="heading-3 text-stone-900 mb-8 text-center">Explore Other Partners</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {otherPartners.map((p) => (
              <Link
                key={p.id}
                to={`/partners/${p.slug}`}
                className="group p-6 border border-stone-200 hover:border-stone-300 transition-colors"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                  <span className="font-medium">{p.rating}</span>
                </div>
                <h3 className="font-heading text-lg text-stone-900 mb-2 group-hover:text-amber-800 transition-colors">
                  {p.name}
                </h3>
                <p className="text-sm text-stone-600">{p.specialty}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartnerDetailPage;
